import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminListTranslatorApplications, adminDecideTranslatorApplication } from "@/lib/apiClient";
import { Languages, X } from "lucide-react";

const STATUS_FILTERS = ["new", "reviewing", "accepted", "rejected", "all"];
const STATUS_LABEL = { new: "New", reviewing: "Reviewing", accepted: "Accepted", rejected: "Rejected" };
const STATUS_CLS = {
  new: "bg-[#FFF7ED] text-[#D97706] border-[#FED7AA]",
  reviewing: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  accepted: "bg-[#ECFDF5] text-[#064E3B] border-[#A7F3D0]",
  rejected: "bg-[#FEF2F2] text-[#EF4444] border-[#FECACA]",
};

export default function AdminTranslatorApplications() {
  const [status, setStatus] = useState("new");
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null); // the application row currently open in the detail modal

  const load = () => {
    adminListTranslatorApplications(status === "all" ? null : status)
      .then((r) => setItems(r.items || []))
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const setApplicationStatus = async (id, next) => {
    setBusyId(id);
    try {
      await adminDecideTranslatorApplication(id, next);
      toast.success(`Marked as ${STATUS_LABEL[next].toLowerCase()}`);
      setDetail((d) => (d && d.application_id === id ? { ...d, status: next } : d));
      // A status change can move the row out of the currently-filtered list
      // (e.g. New -> Reviewing while viewing the "New" filter) — reload
      // rather than patch in place so the list always matches the filter.
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update this application");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-translator-applications">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937] inline-flex items-center gap-2">
        <Languages className="w-6 h-6 text-[#064E3B]" /> Translator Applications
      </h1>
      <p className="text-sm text-[#4B5563] mt-2">Review volunteer applications to translate the app into a new language.</p>

      <div className="mt-5 inline-flex items-center rounded-full border border-[#E7E0D2] bg-white overflow-hidden">
        {STATUS_FILTERS.map((s) => (
          <button key={s} data-testid={`admin-tapp-filter-${s}`} onClick={() => setStatus(s)}
            className={`px-3.5 py-1.5 text-xs font-medium capitalize transition ${
              status === s ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"
            }`}>
            {s}
          </button>
        ))}
      </div>

      <div className="mt-4 rounded-2xl bg-white border border-[#E7E0D2] overflow-hidden">
        {items === null ? (
          <div className="p-6 text-sm text-[#4B5563]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-[#4B5563]">No {status !== "all" ? status : ""} applications.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FDFBF7] text-[#4B5563] text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Applicant</th>
                  <th className="px-4 py-3 font-medium">Language</th>
                  <th className="px-4 py-3 font-medium">Proficiency</th>
                  <th className="px-4 py-3 font-medium">Experience</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.application_id} data-testid={`admin-tapp-row-${a.application_id}`}
                    onClick={() => setDetail(a)}
                    className="border-t border-[#E7E0D2] hover:bg-[#FDFBF7] cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1F2937]">{a.full_name}</div>
                      <div className="text-xs text-[#9CA3AF]">{a.email}</div>
                    </td>
                    <td className="px-4 py-3 text-[#1F2937]">{a.target_language_label || a.target_language}</td>
                    <td className="px-4 py-3 text-[#4B5563] capitalize">{a.proficiency}</td>
                    <td className="px-4 py-3 text-[#4B5563]">{a.has_experience ? "Yes" : "No"}</td>
                    <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap">{new Date(a.submitted_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-medium ${STATUS_CLS[a.status]}`}>
                        {STATUS_LABEL[a.status] || a.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <ApplicationDetailModal
          application={detail}
          busy={busyId === detail.application_id}
          onStatus={(next) => setApplicationStatus(detail.application_id, next)}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function ApplicationDetailModal({ application: a, busy, onStatus, onClose }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7E0D2] sticky top-0 bg-white">
          <div className="font-heading text-lg text-[#1F2937]">{a.full_name}</div>
          <button onClick={onClose} className="text-[#4B5563] hover:text-[#1F2937]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <DetailRow label="Email" value={a.email} />
          <DetailRow label="Mobile / WhatsApp" value={a.mobile_number} />
          <DetailRow label="Target language" value={a.target_language_label || a.target_language} />
          <DetailRow label="Proficiency" value={a.proficiency} capitalize />
          <DetailRow label="Previous experience" value={a.has_experience ? "Yes" : "No"} />
          {a.has_experience && (
            <DetailRow label="Experience description" value={a.experience_description || "—"} block />
          )}
          <DetailRow label="Submitted" value={new Date(a.submitted_at).toLocaleString()} />

          <div>
            <div className="text-xs uppercase tracking-wider text-[#4B5563] mb-2">Status</div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(STATUS_LABEL).map((s) => (
                <button key={s} disabled={busy || a.status === s} onClick={() => onStatus(s)}
                  data-testid={`admin-tapp-status-${s}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    a.status === s
                      ? STATUS_CLS[s]
                      : "bg-white text-[#4B5563] border-[#E7E0D2] hover:border-[#064E3B] hover:text-[#064E3B]"
                  }`}>
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, capitalize, block }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#4B5563]">{label}</div>
      <div className={`mt-0.5 text-sm text-[#1F2937] ${capitalize ? "capitalize" : ""} ${block ? "whitespace-pre-wrap leading-relaxed" : ""}`}>
        {value}
      </div>
    </div>
  );
}
